import {
  BADGE_DESCRIPTIONS,
  BADGE_IMAGES,
  type AchievementBadge,
  type AwardedBadge,
  type BadgeModuleAward,
  type BadgesResponse,
  type DrawerBadge,
  type ModuleAward,
  type ModuleInfo,
} from './badges';

/**
 * The per-module breakdown for an awarded badge. Prefers `module_awards`; falls
 * back to synthesizing a single entry from the legacy `module_id` when the
 * breakdown is absent (older payloads / badges that only carry `module_id`).
 */
const resolveModuleAwards = (earned?: AwardedBadge): ModuleAward[] => {
  if (!earned) return [];
  if (earned.module_awards && earned.module_awards.length > 0) {
    return earned.module_awards;
  }
  if (earned.module_id) {
    return [
      {
        module_id: earned.module_id,
        module_name: '',
        awarded_count: earned.awarded_count,
      },
    ];
  }
  return [];
};

/**
 * Merge `eligible_badges` (the badges to show) with `awarded_badges` (the
 * student's earned counts) into the UI shape. Iterates eligible-only, so the
 * drawer never shows badges outside the student's eligible set. `moduleNameById`
 * is an optional fallback for module titles when the response omits them.
 */
export const toDrawerBadges = (
  res: BadgesResponse,
  moduleNameById?: Record<string, string>,
  moduleNumberById?: Record<string, string>
): DrawerBadge[] => {
  const awarded = new Map(
    res.awarded_badges.map(badge => [badge.badge_id, badge])
  );
  const badges = res.eligible_badges.map(eligible => {
    const earned = awarded.get(eligible.badge_id);
    const moduleAwards: BadgeModuleAward[] = resolveModuleAwards(earned)
      .filter(award => award.module_id)
      .map(award => ({
        moduleId: award.module_id,
        moduleName: award.module_name || moduleNameById?.[award.module_id],
        moduleNumber: moduleNumberById?.[award.module_id],
        count: award.awarded_count,
      }));
    return {
      badgeId: eligible.badge_id,
      name: eligible.badge_name,
      imageUrl: BADGE_IMAGES[eligible.badge_id] ?? '',
      description:
        BADGE_DESCRIPTIONS[eligible.badge_id] ?? eligible.description,
      category: eligible.category,
      type: eligible.type,
      count: earned?.awarded_count ?? 0,
      isLocked: !earned,
      moduleAwards,
      primaryModuleId: earned?.module_id,
      awardedOn: earned?.awarded_on,
    };
  });
  // Earned first, then locked system/auto badges, then locked professor/manual badges.
  const unlocked = badges.filter(b => !b.isLocked);
  const lockedAuto = badges.filter(b => b.isLocked && b.type === 'auto');
  const lockedManual = badges.filter(b => b.isLocked && b.type === 'manual');
  return [...unlocked, ...lockedAuto, ...lockedManual];
};

/**
 * Pick up to `count` badges for the dashboard preview strip: earned (unlocked)
 * first, then locked fillers so the strip always shows `count` tiles.
 */
export const selectPreviewBadges = (
  badges: DrawerBadge[],
  count = 3
): AchievementBadge[] => {
  const unlocked = badges
    .filter(badge => !badge.isLocked)
    .sort((a, b) => (b.awardedOn ?? 0) - (a.awardedOn ?? 0));
  const locked = badges.filter(badge => badge.isLocked);
  return [...unlocked, ...locked]
    .slice(0, count)
    .map(({ imageUrl, name, isLocked }) => ({ imageUrl, name, isLocked }));
};

type ModuleEntry = {
  title: string;
  moduleNumber: number;
  /**
   * Latest `awardedOn` among badges whose PRIMARY module is this one. Used to
   * order modules in the fallback (no module numbers). Stays 0 for a module that
   * only appears as a secondary in another badge's `moduleAwards`.
   */
  latestAwardedOn: number;
  badges: DrawerBadge[];
};

/** Derive a display title and numeric sort key from a single module award. */
const resolveModuleEntry = (
  award: BadgeModuleAward
): { title: string; moduleNumber: number } => {
  const parsed = award.moduleNumber
    ? Number.parseInt(award.moduleNumber, 10)
    : Number.NaN;
  const moduleNumber = Number.isNaN(parsed) ? -1 : parsed;
  const title =
    !Number.isNaN(parsed) && award.moduleName
      ? `Module ${award.moduleNumber} - ${award.moduleName}`
      : (award.moduleName ?? 'Module');
  return { title, moduleNumber };
};

/** Upsert a badge into the per-module map, refreshing title and number when better data arrives. */
const upsertModuleEntry = (
  byModule: Map<string, ModuleEntry>,
  award: BadgeModuleAward,
  badge: DrawerBadge
): void => {
  const { title, moduleNumber } = resolveModuleEntry(award);
  const entry = byModule.get(award.moduleId) ?? {
    title,
    moduleNumber,
    latestAwardedOn: 0,
    badges: [],
  };
  entry.badges.push({ ...badge, count: award.count });
  if (moduleNumber >= 0) entry.moduleNumber = moduleNumber;
  if (award.moduleName) entry.title = title;
  // Attribute the award timestamp to the badge's primary module only.
  if (badge.primaryModuleId === award.moduleId) {
    entry.latestAwardedOn = Math.max(
      entry.latestAwardedOn,
      badge.awardedOn ?? 0
    );
  }
  byModule.set(award.moduleId, entry);
};

/**
 * Group earned, module-bound badges into per-module sections. The
 * `currentModuleId` (the same module the "Current Module Badges" counter
 * reflects) is pinned on top so the counter and the first section always match.
 * Remaining modules are ordered by module number desc, falling back to
 * most-recently-awarded first when numbers are unavailable. Locked and
 * non-module badges are excluded.
 */
export const groupBadgesByModule = (
  badges: DrawerBadge[],
  currentModuleId?: string
): Array<{ module: ModuleInfo; badges: DrawerBadge[] }> => {
  const byModule = new Map<string, ModuleEntry>();

  for (const badge of badges) {
    if (badge.isLocked) continue;
    // A badge can belong to several modules — add it to each, carrying that
    // module's own count so the per-module red dot is accurate.
    for (const award of badge.moduleAwards) {
      if (!award.moduleId) continue;
      upsertModuleEntry(byModule, award, badge);
    }
  }

  return [...byModule.entries()]
    .sort(([aId, a], [bId, b]) => {
      // Pin the current module on top so it matches the counter.
      if (aId === currentModuleId) return -1;
      if (bId === currentModuleId) return 1;
      // Then highest module number first; tie-break on most-recent award.
      if (b.moduleNumber !== a.moduleNumber) {
        return b.moduleNumber - a.moduleNumber;
      }
      return b.latestAwardedOn - a.latestAwardedOn;
    })
    .map(([id, entry]) => ({
      module: { id, title: entry.title },
      badges: entry.badges,
    }));
};
